import styled from "styled-components";
import { TablePageContainer } from "components/TablePageLayout";
import { ModalOverlayBase, ModalSurfaceBase } from "components/Modals/ModalPrimitives";
import { pxToRem, withAlpha } from "../../styles/utils";

export const ProfilePageContainer = styled(TablePageContainer)``;

export const ContentWrapper = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  scrollbar-width: none;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-width: ${pxToRem(1480)};
  box-sizing: border-box;
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} ${({ theme }) => pxToRem(theme.spacing.xxl)} ${({ theme }) => pxToRem(theme.spacing.xxxl)};

  @media (max-width: 760px) {
    padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.xxl)};
  }
`;

export const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xxl)};
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const TableHeaderBar = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => pxToRem(theme.spacing.xl)} 0 ${({ theme }) => pxToRem(theme.spacing.lg)};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
`;

export const PricingScroll = styled.div`
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  overflow: auto;
  padding-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const PricingTable = styled.table`
  width: 100%;
  min-width: ${pxToRem(760)};
  min-height: clamp(${pxToRem(560)}, calc(100vh - ${pxToRem(300)}), ${pxToRem(740)});
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;

  thead {
    position: sticky;
    top: 0;
    z-index: 3;
    background: ${({ theme }) => theme.colors.white};
  }

  th:first-child,
  td:first-child {
    width: 18%;
  }

  @media (max-width: 760px) {
    min-height: ${pxToRem(520)};
  }
`;

export const EmptyHeaderCell = styled.th`
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.xl)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
`;

export const PlanHeader = styled.th<{ $current: boolean }>`
  position: relative;
  overflow: hidden;
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.lg)} ${({ theme }) => pxToRem(theme.spacing.xl)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ $current, theme }) =>
    $current ? `${pxToRem(theme.radii.md)} ${pxToRem(theme.radii.md)} 0 0` : 0};
  background: ${({ $current, theme }) => ($current ? theme.colors.brandSubtle : "transparent")};
  text-align: center;
  vertical-align: top;
`;

export const PlanStatusSpace = styled.div`
  min-height: ${pxToRem(48)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const PlanBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin: 0 ${pxToRem(-18)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  background: ${({ theme }) => theme.colors.brandMedium};
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`;

export const PlanBadgeUntil = styled.span<{ $warning: boolean }>`
  color: ${({ $warning, theme }) => ($warning ? theme.colors.warningText : "inherit")};
  font-weight: 400;
`;

export const PlanName = styled.div`
  margin-bottom: ${pxToRem(7)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
`;

export const PlanPrice = styled.div`
  min-height: ${pxToRem(36)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;

  span {
    color: ${({ theme }) => theme.colors.inkMuted};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    font-weight: 400;
    letter-spacing: 0;
  }
`;

export const PlanDescription = styled.div`
  height: ${pxToRem(56)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.lg)};
  overflow: hidden;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 400;
  line-height: ${pxToRem(19)};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  @media (max-width: 760px) {
    display: none;
    height: 0;
    margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  }
`;

export const PlanAction = styled.div`
  > button {
    width: 100%;
    min-width: 0;
    max-width: none;
    height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    font-weight: 500;
  }
`;

export const FeatureRow = styled.tr`
  &:last-child td {
    border-bottom: 0;
  }
`;

export const FeatureNameCell = styled.td`
  padding: ${({ theme }) => pxToRem(theme.spacing.md)};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
  vertical-align: middle;
`;

export const FeatureCell = styled.td<{ $current: boolean; $lastRow: boolean }>`
  padding: ${({ theme }) => pxToRem(theme.spacing.md)};
  border-bottom: ${({ $lastRow, theme }) =>
    $lastRow ? 0 : `${pxToRem(1)} solid ${theme.borders.divider}`};
  border-radius: ${({ $current, $lastRow, theme }) =>
    $current && $lastRow ? `0 0 ${pxToRem(theme.radii.md)} ${pxToRem(theme.radii.md)}` : 0};
  background: ${({ $current, theme }) => ($current ? theme.colors.brandSubtle : "transparent")};
  text-align: center;
  vertical-align: middle;
`;

export const FeatureYes = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.success};

  svg {
    width: ${pxToRem(22)};
    height: ${pxToRem(22)};
  }
`;

export const FeatureNo = styled.span`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.22)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
`;

export const FootNote = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.lg)} 0 0;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.45;
`;

export const LoadingState = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const ErrorState = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0;
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};

  > button {
    width: auto;
    min-width: ${pxToRem(110)};
    height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  }
`;

export const ModalOverlay = styled(ModalOverlayBase)`
  z-index: 80;
`;

export const ModalCard = styled(ModalSurfaceBase)`
  width: min(100%, ${pxToRem(460)});
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const ModalTitle = styled.h3`
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
`;

export const ModalText = styled.p`
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const ModalSummary = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};

  > div {
    display: flex;
    justify-content: space-between;
    gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
    padding: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0;
  }

  span {
    color: ${({ theme }) => theme.colors.inkSoft};
  }

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
    text-align: right;
  }
`;

export const ModalSetting = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} 0;
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const SettingName = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
`;

export const SettingNote = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;

export const AgreementText = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.md)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.45;

  a {
    color: ${({ theme }) => theme.colors.brand};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
      outline-offset: ${pxToRem(2)};
    }
  }
`;

export const ModalActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};

  > button {
    width: auto;
    min-width: 0;
    height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    white-space: nowrap;
  }
`;

export const ModalActionsSpacer = styled.span`
  min-width: ${({ theme }) => pxToRem(theme.spacing.xs)};
  flex: 1 1 auto;
`;
